import type { AutomaticWebsiteAssessment, Business, PagespeedSummary } from '../types';

/**
 * Lectures tolerantes des deux champs d'analyse.
 *
 * Le frontend tourne **en local** et le backend est **heberge** : rien ne
 * garantit qu'ils soient a la meme version. Pendant un deploiement, ou si la
 * mise en ligne tarde, un backend plus ancien ne renvoie pas encore ces champs
 * — ils valent alors `undefined`, pas `null`.
 *
 * Sans ces lectures, `pagespeed === null` etait faux pour un champ absent : le
 * dossier annoncait « tous analyses » et desactivait l'analyse groupee, sur des
 * sites qui n'avaient jamais ete analyses. Constate en conditions reelles.
 */
export function pagespeedOf(business: Business): PagespeedSummary | null {
  return business.pagespeed ?? null;
}

export function assessmentOf(business: Business): AutomaticWebsiteAssessment {
  return business.automaticAssessment ?? 'NOT_ANALYZED';
}

/**
 * Meme regle que `server/src/domain/websiteAssessment.ts`, cote client.
 *
 * Le serveur reste la source de verite : c'est lui qui remplit
 * `automaticAssessment` dans les resultats de recherche. Cette copie ne sert
 * qu'a une chose — reclasser immediatement une fiche apres une analyse
 * individuelle, sans relancer toute la recherche. Les seuils doivent rester
 * identiques des deux cotes ; ils sont testes ici comme la-bas.
 *
 * Ne jamais confondre avec `websiteQuality`, le jugement humain, qui reste
 * prioritaire a l'affichage comme au classement.
 */
export const ASSESSMENT_THRESHOLDS = {
  performanceBad: 50,
  performanceCheck: 80,
  averageBad: 55,
  averageCheck: 80,
} as const;

function moyenne(summary: PagespeedSummary): number | null {
  const valeurs = [
    summary.performance,
    summary.accessibility,
    summary.bestPractices,
    summary.seo,
  ].filter((valeur): valeur is number => typeof valeur === 'number');

  if (valeurs.length === 0) return null;
  return valeurs.reduce((total, valeur) => total + valeur, 0) / valeurs.length;
}

export function assessLocally(
  summary: PagespeedSummary | null,
): AutomaticWebsiteAssessment {
  if (summary === null) return 'NOT_ANALYZED';

  const moyenneScores = moyenne(summary);
  if (moyenneScores === null) return 'NOT_ANALYZED';

  const performance = summary.performance;

  if (
    (performance !== null && performance < ASSESSMENT_THRESHOLDS.performanceBad) ||
    moyenneScores < ASSESSMENT_THRESHOLDS.averageBad
  ) {
    return 'LIKELY_BAD';
  }

  if (
    (performance !== null && performance < ASSESSMENT_THRESHOLDS.performanceCheck) ||
    moyenneScores < ASSESSMENT_THRESHOLDS.averageCheck
  ) {
    return 'TO_CHECK';
  }

  return 'TECHNICALLY_OK';
}
